import React from 'react';
import { Dot, Image } from 'pure-react-carousel';
import { cn } from '@express-labs/raven-tools';
import { CarouselInjectedProps } from 'pure-react-carousel';
import s from './HorizontalThumbnails.scss';

const THUMBNAILS_TO_SHOW = 7;

export type HorizontalThumbnailsProps = {
  // an array of image URL's.  Passed in from Carousel
  images?: string[];

  // You must pass in the current device width in pixels.
  deviceWidth?: number;

  thumbnailsToShow?: number;

  // The position of the thumbnails in relation to the main carousel
  position?: string;
};

interface ViewModelInjectedProps extends CarouselInjectedProps {
  // Passed in via WithStore() HOC. The index of the leftmost slide in the carousel.
  currentSlide?: number;
}

export default class HorizontalThumbnails extends React.Component<
  HorizontalThumbnailsProps & ViewModelInjectedProps
> {
  public refViewport: React.RefObject<HTMLDivElement> = React.createRef();
  public refSlider: React.RefObject<HTMLDivElement> = React.createRef();

  public thumbnailParentWidth = HorizontalThumbnails.getThumbnailParentWidth(
    this.props.images || [],
    this.props.thumbnailsToShow || THUMBNAILS_TO_SHOW
  );
  constructor(props: HorizontalThumbnailsProps & ViewModelInjectedProps) {
    super(props);
  }

  static defaultProps = {
    images: [''],
    thumbnailsToShow: 7,
    position: 'bottom',
  };

  componentDidMount() {
    this.refSlider.current?.setAttribute('style', this.getSliderStyle());
  }

  componentDidUpdate() {
    const { images = [], thumbnailsToShow = THUMBNAILS_TO_SHOW } = this.props;
    this.thumbnailParentWidth = HorizontalThumbnails.getThumbnailParentWidth(
      images,
      thumbnailsToShow
    );
    this.refSlider.current?.setAttribute('style', this.getSliderStyle());
  }

  static getThumbnailParentWidth(images: string[], thumbnailsToShow: number) {
    return images.length < thumbnailsToShow
      ? 100
      : (100 * images.length) / thumbnailsToShow;
  }

  static getThumbnailWidth(sliderWidth: number, imagesLength: number) {
    return sliderWidth / imagesLength;
  }

  static getLeftPosition(currentSlide: number, thumbnailWidth: number) {
    return -1 * (currentSlide - 1) * thumbnailWidth;
  }

  getSliderStyle() {
    const { images = [], currentSlide = 0 } = this.props;
    // Using getBoundingClientRect() instead of clientHeight because clientHeight rounds to nearest
    // whole number and therefore is less accurate on retina or other high pixel density devices. We
    // need to be accurate to a fraction of a pixel to avoid floated thumbnails wrapping
    // prematurely.
    const viewportWidth =
      this.refViewport.current?.getBoundingClientRect().width || 0;
    const sliderWidth =
      this.refSlider.current?.getBoundingClientRect().width || 0;
    const thumbnailWidth = HorizontalThumbnails.getThumbnailWidth(
      sliderWidth,
      images.length
    );
    const leftPosition = HorizontalThumbnails.getLeftPosition(
      currentSlide,
      thumbnailWidth
    );
    const rightCheck = sliderWidth + leftPosition;

    let retVal = `width: ${this.thumbnailParentWidth}%;`;

    // if the slider is not filling/overflowing its container, we don't need to position the slider
    if (sliderWidth <= viewportWidth) return retVal;

    // moves the slider left or right inside its parent to position the selected image in focus
    if (rightCheck < viewportWidth) {
      retVal += ` left: -${sliderWidth - viewportWidth}px;`;
    } else if (currentSlide > 0) {
      retVal += ` left: ${leftPosition}px;`;
    }

    return retVal;
  }

  render() {
    const { images = [], position = 'bottom' } = this.props;

    return (
      <div className={s.viewport} ref={this.refViewport}>
        <div className={s.slider} ref={this.refSlider}>
          {images.map((image, i) => (
            <Dot
              className={cn(s.thumbnail, position)}
              key={image}
              slide={i}
              disabled={false}
            >
              <Image hasMasterSpinner={false} src={image} />
            </Dot>
          ))}
        </div>
      </div>
    );
  }
}
